'use client';

import { useEffect, useCallback, useMemo, useState } from 'react'
import type { 
  CalculatorId, 
  CalculatorConfig, 
  ControlId,
  ControlValues,
  CalculatorTypes,
  CalculatorControl,
  CalculatorIdType
} from '../../lib/calculators/calculator-config'
import { DataTableInputMode } from '../../lib/calculators/calculator-config'
import { createCalculatorStore } from '../../lib/calculators/calculator-store'
import { DataPanel } from './calculator-layout/data-panel'
import { ResultPanel } from './calculator-layout/result-panel'
import { ConfigPanel } from './calculator-layout/config-panel'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui'
import { Card } from '@/components/ui'
import { useStore } from 'zustand'
import { TableDataJSON } from '../../src/types/table-json'

export interface CalculatorProps<T extends CalculatorId> {
  config: CalculatorConfig<T>;
  maxHeight?: number;
}

// Single store instance
const store = createCalculatorStore()

// Stable selectors for state
const selectData = (state: ReturnType<typeof store.getState>) => state.data
const selectControlValues = (state: ReturnType<typeof store.getState>) => state.controlValues
const selectVisualizationData = (state: ReturnType<typeof store.getState>) => state.visualizationData

export function Calculator<T extends CalculatorId>({ config, maxHeight }: CalculatorProps<T>) {
  // Get state
  const data = useStore(store, selectData)
  const controlValues = useStore(store, selectControlValues)
  const visualizationData = useStore(store, selectVisualizationData)
  
  // Add loading state
  const [isInitialized, setIsInitialized] = useState(false)
  
  // Stable actions object
  const actions = useMemo(() => ({
    setData: store.getState().setData,
    setControlValue: store.getState().setControlValue,
    setConfig: store.getState().setConfig,
    resetControlValues: store.getState().resetControlValues
  }), [])

  // Memoize initial data transformations
  const initialDataProps = useMemo(() => {


    if (!config.initialData || !(config.initialData instanceof TableDataJSON)) {
      return {
        columns: [],
        initialData: undefined
      };
    }

    // Add widths to columns but preserve TableDataJSON instance
    const columnsWithWidths = config.initialData.columns.map(col => ({ 
      ...col, 
      width: 150 
    }));

    // Create new TableDataJSON with updated column widths
    const dataWithWidths = new TableDataJSON(
      columnsWithWidths.map(col => ({
        id: col.id,
        name: col.name,
        values: config.initialData?.columns.find(c => c.id === col.id)?.values || []
      }))
    );

    return {
      columns: columnsWithWidths,
      initialData: dataWithWidths
    };
  }, [config.initialData])

  // Set initial data and config on mount
  useEffect(() => {
    const initializeCalculator = async () => {
      try {
        // First set the data
        if (initialDataProps.initialData) {
          await actions.setData(initialDataProps.initialData)
        }
        
        // Then set the config to ensure control values are initialized with the data
        await actions.setConfig(config)
        
        // Mark as initialized
        setIsInitialized(true)
      } catch (error) {
        console.error('Failed to initialize calculator:', error)
        // Still mark as initialized to avoid infinite loading
        setIsInitialized(true)
      }
    }
    
    initializeCalculator()
  }, [config, actions, initialDataProps.initialData])

  // Show loading state while initializing
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center w-full h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
          <p className="text-muted-foreground">Loading calculator...</p>
        </div>
      </div>
    )
  }

  // Render panels
  const dataPanel = (
    <DataPanel
      onDataChange={actions.setData}
      initialColumns={initialDataProps.columns}
      initialData={initialDataProps.initialData}
      maxHeight={maxHeight}
      resetControlValues={actions.resetControlValues}
    />
  )

  const resultPanel = (
    <ResultPanel className="h-full">
      {config.render?.({ visualizationData: visualizationData as CalculatorTypes[T]['visualization'] | null }) || (
        <div className="flex items-center justify-center w-full h-full border border-dashed border-border rounded-lg">
          {config.dataTableInputMode !== DataTableInputMode.None && !data ? (
            <p className="text-muted-foreground">Please load data to see results</p>
          ) : (
            <p className="text-muted-foreground">No visualization available</p>
          )}
        </div>
      )}
    </ResultPanel>
  )

  const configPanel = (config.dataTableInputMode === DataTableInputMode.None || data) ? (
    <ConfigPanel
      controls={config.controls.tabs.flatMap(tab => tab.controls)}
      values={controlValues}
      onChange={(id: string, value: unknown) => {
        if (config.controls.tabs.some(tab => 
          tab.controls.some(control => control.id === id)
        )) {
          actions.setControlValue(id as ControlId, value as string | string[])
        }
      }}
      data={data}
      tabs={config.controls.tabs.map(tab => ({
        id: tab.title.toLowerCase(),
        label: tab.title,
        controls: tab.controls.map(control => control.id)
      }))}
    />
  ) : (
    <Card className="h-full flex items-center justify-center">
      <p className="text-muted-foreground">Please load data to configure the calculator</p>
    </Card>
  )

  return (
    <>
      {/* Desktop Layout */}
      <div className="hidden md:grid h-full gap-2 max-w-full min-h-0" style={{ 
        gridTemplateColumns: config.dataTableInputMode === 'none' ? '2fr 5fr' : '2fr 5fr 2fr',
        gridTemplateAreas: config.dataTableInputMode === 'none' ? '"config viz"' : '"data viz config"',
        gridTemplateRows: '1fr',
        height: '100%'
      }}>
        {config.dataTableInputMode !== 'none' && (
          <div className="h-full min-h-0 overflow-hidden" style={{ gridArea: 'data' }}>{dataPanel}</div>
        )}
        {config.dataTableInputMode === 'none' ? (
          <>
            <div className="h-full min-h-0 overflow-hidden" style={{ gridArea: 'config' }}>{configPanel}</div>
            <div className="h-full min-h-0 overflow-hidden" style={{ gridArea: 'viz' }}>{resultPanel}</div>
          </>
        ) : (
          <>
            <div className="h-full min-h-0 overflow-hidden" style={{ gridArea: 'viz' }}>{resultPanel}</div>
            <div className="h-full min-h-0 overflow-hidden" style={{ gridArea: 'config' }}>{configPanel}</div>
          </>
        )}
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden h-full flex flex-col">
        <Tabs defaultValue={config.dataTableInputMode === 'none' ? "config" : "graph"} className="h-full flex flex-col">
          <TabsList className="w-full flex border-b">
            {config.dataTableInputMode !== 'none' && (
              <TabsTrigger value="data" className="flex-1">Data</TabsTrigger>
            )}
            <TabsTrigger value="config" className="flex-1">Config</TabsTrigger>
            <TabsTrigger value="graph" className="flex-1">{config.name}</TabsTrigger>
          </TabsList>
          {config.dataTableInputMode !== 'none' && (
            <TabsContent value="data" className="flex-1 h-full m-0 p-2 overflow-hidden">
              <div className="h-full overflow-auto">{dataPanel}</div>
            </TabsContent>
          )}
          <TabsContent value="config" className="flex-1 h-full m-0 p-2 overflow-hidden">
            <div className="h-full overflow-auto">{configPanel}</div>
          </TabsContent>
          <TabsContent value="graph" className="flex-1 h-full m-0 p-2 overflow-hidden">
            <div className="h-full overflow-auto">{resultPanel}</div>
          </TabsContent>
        </Tabs>
      </div>
    </>
  )
} 